import { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  ClipboardList,
  Grid2x2Plus,
  SearchCheck,
  Target,
} from 'lucide-react';
import { useCRM } from '../../../hooks/crm';

const UserCRMSummary = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const {
    boards,
    selectedBoard,
    totalBoards,
    totalInvestorsInResearch,
    loading,
    error,
  } = useCRM();

  const languagePrefix = i18n.language === 'en' ? '' : `/${i18n.language}`;
  const crmPath = `${languagePrefix}/app/crm${location.search || ''}`;

  const totalSavedInvestors = useMemo(
    () => boards.reduce((total, board) => (
      total + (board?.columns || []).reduce(
        (columnTotal, column) => columnTotal + (column?.investors || []).length,
        0
      )
    ), 0),
    [boards]
  );

  const summaryCards = [
    {
      icon: Grid2x2Plus,
      label: t('app:dashboard.crmSummary.boards'),
      value: totalBoards,
    },
    {
      icon: ClipboardList,
      label: t('app:dashboard.crmSummary.savedInvestors'),
      value: totalSavedInvestors,
    },
    {
      icon: SearchCheck,
      label: t('app:dashboard.crmSummary.researchInvestors'),
      value: totalInvestorsInResearch,
    },
  ];

  if (loading) {
    return (
      <section className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm">
        <p className="text-sm text-stone-500">{t('app:dashboard.crmSummary.loading')}</p>
      </section>
    );
  }

  if (error) {
    return (
      <section className="rounded-lg border border-red-100 bg-white p-5 shadow-sm">
        <p className="text-sm text-red-500">{t('app:dashboard.crmSummary.error')}</p>
      </section>
    );
  }

  return (
    <section className="rounded-lg border border-stone-200 bg-white p-5 shadow-sm">
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <div className="mb-2 inline-flex items-center gap-2 rounded-full border border-stone-200 bg-stone-50 px-3 py-1 text-xs font-medium text-stone-600">
            <Target className="h-3.5 w-3.5" />
            {t('app:dashboard.crmSummary.badge')}
          </div>
          <h3 className="text-xl font-semibold tracking-tight text-stone-950">
            {t('app:dashboard.t1')}
          </h3>
          <p className="mt-2 text-sm leading-6 text-stone-600">
            {totalSavedInvestors > 0
              ? t('app:dashboard.crmSummary.subtitleActive', {
                boardName: selectedBoard?.board_name || t('app:dashboard.crmSummary.defaultBoard'),
              })
              : t('app:dashboard.crmSummary.subtitleEmpty')}
          </p>
        </div>

        <Link
          to={crmPath}
          className="inline-flex h-8 shrink-0 items-center justify-center gap-2 rounded-full border border-stone-200 bg-white px-3 text-xs font-medium text-stone-700 shadow-sm transition-colors hover:bg-stone-50 hover:text-stone-950"
        >
          <span>{t('app:dashboard.crmSummary.openCrm')}</span>
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </div>

      <div className="mt-5 grid gap-3 sm:grid-cols-3">
        {summaryCards.map(({ icon: Icon, label, value }) => (
          <div
            key={label}
            className="rounded-lg border border-stone-200 bg-stone-50/70 p-4"
          >
            <div className="mb-3 inline-flex h-8 w-8 items-center justify-center rounded-md bg-white text-stone-700 shadow-sm ring-1 ring-stone-200">
              <Icon className="h-4 w-4" />
            </div>
            <div className="text-2xl font-semibold tracking-tight text-stone-950">
              {Number(value || 0).toLocaleString()}
            </div>
            <div className="mt-1 text-xs leading-5 text-stone-600">{label}</div>
          </div>
        ))}
      </div>

      {boards.length > 0 ? (
        <div className="mt-4 rounded-lg border border-stone-200 bg-white px-4 py-3">
          <div className="text-xs font-medium uppercase text-stone-500">
            {t('app:dashboard.crmSummary.boardsLabel')}
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            {boards.slice(0, 4).map((board, index) => (
              <span
                key={board?.board_id ?? `${board?.board_name || 'board'}-${index}`}
                className="inline-flex max-w-full items-center rounded-full bg-stone-100 px-3 py-1 text-xs font-medium text-stone-700"
              >
                <span className="truncate">{board?.board_name || t('app:dashboard.crmSummary.defaultBoard')}</span>
              </span>
            ))}
            {boards.length > 4 ? (
              <span className="inline-flex items-center rounded-full bg-stone-100 px-3 py-1 text-xs font-medium text-stone-500">
                {t('app:dashboard.crmSummary.moreBoards', { count: boards.length - 4 })}
              </span>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="mt-4 rounded-lg border border-dashed border-stone-300 bg-stone-50/70 px-4 py-3 text-sm leading-6 text-stone-600">
          {t('app:dashboard.crmSummary.emptyHint')}
        </div>
      )}
    </section>
  );
};

export default UserCRMSummary;
